import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssinanteForm } from "./AssinanteForm";
import { EmpresaCreate, EmpresaUpdate } from "../../types/empresa";
import { empresaService } from "../../services/empresaService";

export const NovoAssinante: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedPlanoId, setSelectedPlanoId] = useState<number | null>(null);

  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (data: EmpresaCreate | Partial<EmpresaUpdate>) => {
    setLoading(true);
    try {
      const dataToSubmit = { ...data } as EmpresaCreate;
      if (selectedPlanoId) {
        dataToSubmit.plano = selectedPlanoId; // Adiciona o plano ao objeto a ser enviado
      }

      const response: any = await empresaService.criar(dataToSubmit);
      // Prefer setup_link if disponível; senão, use setup_token (ou apenas exiba o token)
      if (response.setup_link) {
        setGeneratedLink(response.setup_link);
      } else if (response.setup_token) {
        setGeneratedToken(response.setup_token);
      } else if (
        response.setup_link === undefined &&
        response.setup_token === undefined &&
        response.setup_link == null
      ) {
        // fallback: se backend retornar setup_token como 'setup_token' dentro de 'data' ou outro campo
        const token =
          (response as any).setup_token || (response as any).setupToken || null;
        if (token) setGeneratedToken(token);
      }

      // Não navegamos imediatamente — mostramos o link/token gerado para copiar/enviar
    } catch (error: any) {
      console.error("Erro ao criar assinante:", error);
      const mensagemErro =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Ocorreu um erro ao criar o assinante. Verifique os dados e tente novamente.";
      setErrorMessage(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Novo Assinante</h1>

      {/* Banner de Erro */}
      {errorMessage && (
        <div
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">
                Erro ao Criar Assinante
              </h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-600 hover:text-red-800 text-xl leading-none flex-shrink-0"
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {generatedLink || generatedToken ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-6 text-center">
          <h2 className="text-lg font-bold text-green-800">
            Assinante Criado com Sucesso!
          </h2>
          <p className="mt-2 text-sm text-green-700">
            Copie e envie o link/token abaixo para o administrador do assinante
            configurar a senha inicial.
          </p>

          <div className="mt-4 bg-white p-4 rounded border border-gray-300">
            <input
              type="text"
              value={generatedLink ?? generatedToken ?? ""}
              readOnly
              className="w-full bg-transparent border-none text-gray-700 focus:ring-0"
            />
          </div>

          <div className="mt-4 flex justify-center gap-3">
            <button
              onClick={() => {
                const text = generatedLink ?? generatedToken ?? "";
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
            >
              {copied ? "Copiado!" : "Copiar"}
            </button>

            <button
              onClick={() => {
                // voltar para a listagem de assinantes
                window.location.href = "/assinantes";
              }}
              className="ml-2 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Voltar
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow-md">
          <AssinanteForm
            onSubmit={handleSubmit}
            loading={loading}
            onPlanSelected={setSelectedPlanoId}
          />
        </div>
      )}
    </div>
  );
};